export const meta = {
  id: '09-aquatic-and-terrestrial-classes',
  order: 9,
  checkFocus: { cases: ['acc'] },   // what the end-of-lesson check tests first
  titleFi: 'Vesisuku ja maasuku',
  titleEn: 'Aquatic and terrestrial classes',
  duolingoUnit: ['duo-08-aquatic-class', 'duo-09-terrestrial-class'],
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: ['targārien', 'ours:aneviapos', 'ours:vamīhy', 'ours:ezalbar', 'ours:parmurlion', 'ours:lopenka', 'ours:grevenkuragon', 'ours:geralbītsos', 'ours:terrēlaksy', 'ours:betula', 'ours:ambulansi', 'ours:rekka', 'ours:helikopteri', 'ours:ūndralbar'],
  exampleIds: [
    'hotd-s1e07-005',
    'ex-f6095ea6445a',
    'ex-cc3d5caead9a',
    'ex-473562e43757',
    'ex-51968cccd9d6',
    'ex-4db2fcae52a3',
    'ex-e71a1a97c925',
    'ex-55906d4972e7',
    'hotd-s3e05-012',
    'ex-3d0ef75d92bc',
    'hotd-s2e04-001',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Aquatic_class',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Noun_Declensions',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Gender',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Pronouns#Demonstrative_and_interrogative_pronouns',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Gender',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Conjunctive_lengthening',
    'https://wiki.languageinvention.com/index.php?title=Elilla',
    'https://wiki.languageinvention.com/index.php?title=Ārilla',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Terrestrial_class',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Introduction_to_gender',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Basics_2',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Gender',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Noun_Declensions',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Pronouns',
    'https://wiki.languageinvention.com/index.php?title=Targārien',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_pronunciation',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammarAlso: ['genders', 'declensions', 'coordination'],
  showLines: ['hotd-s1e07-005', 'hotd-s3e05-012', 'hotd-s2e04-001'],
  pronunciation: { focus: 'r', words: ['Targārien', 'embar', 'hūra', 'qogror', 'hūrenka'] },
  letter: {
    fi: 'Kuvaile kolmella lauseella, mitä näet ikkunasta: vesi- ja maasukuiset sanat.',
    en: 'In three sentences, describe what you see from the window: aquatic and terrestrial nouns.',
  },
  scene: 'hotd-s1e07-1',
}

# Vesisuku ja maasuku

Loput kaksi sukua: vesisuku, jonka tunnusmerkki on *r*, ja maasuku, jonka tunnusmerkki on *n*. Maasuvun yhteydessä tutustutte nimeen, jonka ympärille koko sarja rakentuu: <Hv>Targārien</Hv>.

## Vesisuku

Duolingon Tips & Notes -ohjeiden mukaan vesisuku on korkeavalyrian pienin <Term id="luokka">substantiiviluokka</Term>. Pieni ei kuitenkaan tarkoita helppoa: ohjeissa sitä kutsutaan haastavaksi, ja syy selviää pian. Suvuissa suomesta ei tässäkään ole apua.

## Tunnusmerkki: r

Ohjeiden mukaan vesisukuisten sanojen päätteisiin liittyy aina *r*. Toisin kuin muissa suvuissa, tämä *r* katoaa harvoin, kun sana taipuu eri sijoissa ja luvuissa. Juuri siksi ohjeissa pidetään vesisukuisten sanojen taivutusta haastavana.

Wikin kieliopin mukaan vesisuvun sanat tarkoittavat usein nesteitä ja vesistöjä: <Hv glossed>iēdar</Hv> ’vesi’, <Hv glossed>ānogar</Hv> ’veri’, <Hv glossed>embar</Hv> ’meri’, <Hv glossed>qelbar</Hv> ’joki’.

<Pronounce word="iēdar" />

<Pronounce word="qelbar" />

<Line id="hotd-s1e07-005" />

Vaemond Velaryonin repliikissä meri esiintyy kahdessa sijamuodossa, jotka opitte myöhemmin: <Hv>embār</Hv> ja <Hv l="embar">embrot</Hv>. Kummassakin *r* on yhä mukana.

## Neljä teemavokaalia

Ohjeiden mukaan vesisukuisilla sanoilla on vain neljä mahdollista teemavokaalia: *a*, *e*, *i* ja *o*. Niistä tulee neljä nominatiivin loppua: *-ar*, *-er*, *-ir* ja *-or*.

- **-ar**: <Hv glossed>embar</Hv> ’meri’, <Hv glossed>qelbar</Hv> ’joki’
- **-er**: <Hv glossed>sēter</Hv> ’loitsu’
- **-ir**: <Hv glossed>rōbir</Hv> ’viikuna’, <Hv glossed>qintir</Hv> ’kilpikonna’
- **-or**: <Hv glossed>lōgor</Hv> ’laiva, vene’, <Hv glossed>qȳbor</Hv> ’eno’

## Monikko: -a tai -i

Ohjeiden mukaan vesisukuiset sanat saavat monikossa tyypillisesti *-a*:n, samoin kuin maasukuiset. Sanakirjassa *-i*-monikko on kuitenkin yleisempi: se on kaikilla *-ar*-sanoilla ja myös niillä vesisuvun sanoilla, jotka päättyvät *-illa*:an, kuten <Hv>ārilla</Hv> → <Hv>ārilli</Hv> (niistä lisää alempana).

<Paradigm lemma="lōgor" cases="nom,acc" numbers="sg,pl" />

<Paradigm lemma="rōbir" cases="nom,acc" numbers="sg,pl" />

<Hv>Lōgor</Hv> → <Seam s="lōg" e="ra" />, <Hv>rōbir</Hv> → <Seam s="rōb" e="ra" />, <Hv>qintir</Hv> → <Seam s="qint" e="ra" />, ja *-er*-sanoissa <Hv>sēter</Hv> → <Hv>sētera</Hv>. Huomaa, että *-or*- ja *-ir*-sanoissa teemavokaali katoaa monikossa *r*:n edeltä, mutta *r* jää. Näissä sanoissa myös akkusatiivi näyttää samalta kuin nominatiivi sekä yksikössä että monikossa.

<Example id="ex-f6095ea6445a" />

<Example id="ex-cc3d5caead9a" />

### -ar-sanat: monikko -i

*-ar*-loppuiset sanat taipuvat wikin taulukoissa toisin. Niiden monikko päättyy *-i*:hin, ja niillä on myös oma akkusatiivinsa:

<Paradigm lemma="embar" cases="nom,acc" numbers="sg,pl" />

<Paradigm lemma="qelbar" cases="nom,acc" numbers="sg,pl" />

<Hv>Embar</Hv> → monikko <Seam s="emb" e="ri" />, yksikön akkusatiivi <Seam s="emb" e="ri" />, monikon akkusatiivi <Seam s="emb" e="rī" />. Kuvio muistuttaa kuusukuisia sanoja, mutta *r* pysyy mukana. Tässäkin suomalaisen korva auttaa: pitkä *ī* erottaa monikon objektin.

## Kun h ja r kohtaavat: rh

Ohjeissa mainitaan erikseen kirjoitusasun muutos, joka tulee vastaan heti tutun ’minun’-sanan kanssa. Vesisuvun yksikössä ’minun’ on <Hv l="ñuha">ñuhor</Hv>. Monikossa *o* putoaa pois, ja tilalle tulee pääte *-a*. Tuloksena pitäisi olla *ñuhra*, mutta *hr* ei ole korkeavalyriassa sallittu yhdistelmä. Siksi sana kirjoitetaan <Hv>ñurha</Hv> ja äännetään sen mukaan.

<Pronounce word="ñurha" />

*rh* on Perusteet 1 -oppitunnilta tuttu soinniton *r*. Sanassa <Hv>ñurha</Hv> se kuulostaa kuiskatulta *r*:ltä *u*:n jälkeen.

<Paradigm lemma="ñuha" filter="positive.pre.aquatic" cases="nom,acc" numbers="sg,pl" />

<Example id="ex-473562e43757" />

Tässä <Hv>ñurha</Hv> kuvaa monikkomuotoista sanaa <Hv glossed>qintra</Hv> ’kilpikonnat’. Edellä olleessa loitsuesimerkissä <Hv glossed>ñurha sētera</Hv> on ’minun loitsuni’.

<Interlinear hv="Riñ|a zōbr|iar lōg|ra urne|s." gloss="tyttö-NOM musta-AQ.ACC.PL laiva-ACC.PL nähdä-PRS.3SG" fi="Tyttö näkee mustat laivat." />

Myös <Hv glossed>zōbriar</Hv> ’mustat’ on vesisuvun monikossa, koska <Hv glossed>lōgor</Hv> ’laiva’ on vesisukuinen.

## Vesisuku ei ole pelkkää vettä

Merkitys antaa vihjeitä, mutta ei ratkaise. Vesisuvussa on myös ihmisiä. Wikin pronominisivu mainitsee esimerkkeinä sanat <Hv glossed>valonqar</Hv> ’pikkuveli’ ja <Hv glossed>hāedar</Hv> ’pikkusisko’, jotka ovat vesisukuisia mutta elollisia.

<Example id="ex-51968cccd9d6" />

<Example id="ex-4db2fcae52a3" />

<Hv glossed>Qȳbor</Hv> ’eno’ ja <Hv glossed>hāedar</Hv> ’pikkusisko’ ovat vesisukuisia, joten ’minun’ ja ’sinun’ ovat vesisuvun muodossa. <Hv>Hāedri</Hv> on akkusatiivi – ja *r* on taas tallella.

Toisaalta kaikki *r*-loppuiset sanat eivät ole vesisukuisia. Wikin kieliopin mukaan **kaikki kollektiivit päättyvät *-r*:ään** suvusta riippumatta. Johdatus sukuihin -oppitunnin <Hv l="qogror">qogror</Hv> on maasukuinen kollektiivi.

### Vesisukuinen ilman r:ää

Wikin sanakirjan mukaan ohjeiden ”aina *r*” ei pidä täysin paikkaansa. Sanakirjassa on vesisukuisia *-illa*-sanoja, jotka taipuvat kuin kuusukuinen <Hv>vala</Hv>: <Hv glossed>elilla</Hv> ’hunaja’, <Hv glossed>ārilla</Hv> ’olut’ ja <Hv glossed>averilla</Hv> ’viini’. Wikin mukaan <Hv>elilla</Hv> on vesisukuinen nimenomaan siksi, että se on nestettä.

<Paradigm lemma="ārilla" cases="nom,acc" numbers="sg,pl" />

<Example id="ex-e71a1a97c925" />

Vaikka <Hv glossed>ārilla</Hv> näyttää kuusukuiselta, ’minun’ on vesisuvun muodossa <Hv l="ñuha">ñuhor</Hv>. Adjektiivi seuraa sanan sukua, ei sen loppua.

## Kertaus: ”ja” ilman ja-sanaa

<Example id="ex-55906d4972e7" />

Tässä ovat vesisukuiset <Hv glossed>jūlor</Hv> ’maito’ ja <Hv glossed>iēdar</Hv> ’vesi’. Ne on liitetty yhteen Perusteet 1 -oppitunnin tapaan: viimeisen sanan loppuvokaali pitenee, <Hv l="iēdar" k="acc.sg">iēdri</Hv> → <Hv k="and.acc.sg">iēdrī</Hv>, eikä ja-sanaa tarvita.

## Harjoitellaan

<Practice hv="Kesy ñuhor lōgor issa." fi="Tämä on minun laivani." />

<Practice hv="Ābra embri urnes." fi="Nainen näkee meren." />

<Practice hv="Kepa ñurha lōgra urnes." fi="Isä näkee laivani." />

<Practice hv="Vala ñuhor hāedri urnes." fi="Mies näkee pikkusiskoni." />

Ensimmäinen lause on tehty samaan malliin kuin osoittavien pronominien oppitunnin <Hv>Kesy ñuhys klios issa</Hv>. Kala on aurinkosukuinen ja laiva vesisukuinen, joten ’minun’ vaihtaa muotoa.

## Maasuku

Neljäs ja viimeinen suku on maasuku, <Hv>tegōñor qogror</Hv>. Se on nimetty sanasta <Hv glossed>tegon</Hv> ’maa, maaperä’, aivan kuten kuusuku on nimetty kuusta ja vesisuku merestä. Duolingon Tips & Notes -ohjeissa maasukua kutsutaan valyrian yksinkertaisimmaksi <Term id="luokka">substantiiviluokaksi</Term>, ja se pitää paikkansa. Kielioppia on vähän, ja loppuaika jää yhdelle nimelle, jonka ympärille koko sarja rakentuu: <Hv>Targārien</Hv>.

## Tuntomerkit: n ja o

Maasuvun sanojen päätteissä on lähes aina **n**, ja niiden teemavokaali on tavallisesti **o**: <Hv glossed>tegon</Hv> ’maa’, <Hv glossed>drōmon</Hv> ’muna’, <Hv glossed>dēmalion</Hv> ’valtaistuin’, <Hv glossed>gierion</Hv> ’kansa’.

Merkitykseltään maasuku on enimmäkseen elottoman maailman suku. Useimmat sen sanat tarkoittavat paikkoja, esineitä, abstrakteja asioita tai tekemistä. Wikin sukusivu lisää kaksi taipumusta: ruoka-aineet ja kasvit ovat usein maasukua, ja metallit ovat sitä yleensä.

<Note>
Muistisääntö Damien Halmelta (Academy of High Valyrian): <Hv glossed>turgon</Hv> ’mato’ on maasukuinen. Harmaa Mato kuuluu siis kieliopissa samaan sukuun kuin maa ja leipä.
</Note>

<Note>
Kertausta: kuusuvun sanat päättyvät useimmiten vokaaliin, aurinkosuvun s:ään, maasuvun n:ään ja vesisuvun r:ään. Wikin mukaan poikkeuksia on kuitenkin paljon, joten sana kannattaa aina opetella yhdessä sukunsa kanssa.
</Note>

## Monikko -a, subjekti ja objekti samanlaiset

<Paradigm lemma="tegon" cases="nom,acc" numbers="sg,pl" />

Taulukosta näkyvät maasuvun kaksi perussääntöä.

**Monikko päättyy -a:han.** <Seam s="teg" e="on" /> muuttuu monikossa muotoon <Seam s="teg" e="a" />, ja <Hv glossed>lenton</Hv> ’talo’ (sana tulee vastaan omistussanojen oppitunnilla) muotoon <Hv l="lenton">lenta</Hv>. Vesisuvussa -a-monikko on vain osalla sanoista (<Hv>bianor</Hv> → <Hv>biādra</Hv>); useimmilla monikko päättyy -i:hin (<Hv>embar</Hv> → <Hv>embri</Hv>, <Hv>ārilla</Hv> → <Hv>ārilli</Hv>).

<Compare fi="talo|t" hv="teg|a" label="monikko: suomen -t ~ maasuvun -a" />

**Nominatiivi ja akkusatiivi ovat samat.** Kuusuvun <Hv glossed>vala</Hv> ’mies’ muuttuu objektina muotoon <Hv>vale</Hv>, mutta <Hv>tegon</Hv> on sama subjektina ja objektina, ja monikossa molemmat ovat <Hv>tega</Hv>.

Suomalaiselle tämä ei ole vierasta. Meilläkin objekti näyttää usein samalta kuin subjekti: *Talo on iso.* – *Osta talo!* – *Näin talot.* Valyriassa sanajärjestys on vapaa, joten sen, kuka tekee mitä kenellekin, näkee sanojen muodoista. Kun maasuvun sana ei itse kerro rooliaan, sen kertoo lauseen toinen substantiivi. Jos toinen on selvästi akkusatiivissa, maasuvun sana on subjekti. Jos toinen on nominatiivissa, maasuvun sana on objekti.

<Practice hv="Ābra tegon urnes." fi="Nainen näkee maan." />

## Missä suomi ei auta

Suomessa ei ole kieliopillista sukua, joten omasta kielestä ei löydy tähän tukea. Sukua ei voi varmasti päätellä merkityksestä, eikä aina edes päätteestä. Hyvä esimerkki on sana <Hv glossed>qogror</Hv> ’luokka, ryhmä, suku’. Se päättyy r:ään kuin vesisuvun sana, mutta wikin sukusivu vahvistaa, että se on maasukua.

Olet itse asiassa nähnyt tämän jo. Neljän suvun nimet ovat <Hv>hūrenkon qogror</Hv>, <Hv>vēzenkon qogror</Hv>, <Hv>tegōñor qogror</Hv> ja <Hv>embōñor qogror</Hv>. Kaikissa neljässä adjektiivi on maasuvun muodossa, koska <Hv l="qogror" fi="luokka, ryhmä, suku">qogror</Hv> on maasukua. <Hv>Hūrenka</Hv> saa maasuvussa muodon <Seam s="hūrenk" e="on" />, ja <Hv>tegōñe</Hv> muodon <Hv>tegōñor</Hv>. Maasuvun adjektiivin pääte ei siis aina ole -on. Se riippuu adjektiivin tyypistä, ja tyyppeihin palataan myöhemmin.

## Targārien

<Pronounce word="Targārien" />

Paino on tavulla <Hv part>gā</Hv>. Sanassa on kolme tavua, koska *ie* on <Term id="nouseva-diftongi">nouseva diftongi</Term>: tar·gā·rien. Toiseksi viimeinen tavu <Hv part>gā</Hv> on raskas, koska siinä on pitkä vokaali, joten se saa painon. Suomalainen vaisto panisi painon ensimmäiselle tavulle, mutta sano tar·GĀ·rien.

Daenerysin etunimi on jo tuttu, nyt on sukunimen vuoro. Duolingon ohjeet muistuttavat, että <Hv>Targārien</Hv> on suvun nimi. Kun sitä käytetään yksinään substantiivina, se tarkoittaa Targaryenien suvun jäsentä. Englanninkielisissä käännöksissä sen edessä on siksi artikkeli, *the Targaryen*, samaan tapaan kuin Romeossa ja Juliassa puhutaan Montaguesta tai Capuletista. Suomessa artikkelia ei tarvita, mutta ajatus on sama: sana nimeää suvun jäsenen eikä ole etunimen kaltainen kutsumanimi.

<Line id="hotd-s3e05-012" />

### Ihminen, mutta maasukua

Ihmisiä tarkoittavat sanat ovat useimmiten kuu- tai aurinkosukua. Wikin pronominisivu mainitsee kuitenkin juuri sanan <Hv>Targārien</Hv> esimerkkinä maasuvun sanasta, joka tarkoittaa elävää olentoa. Suku on kieliopillinen ominaisuus, ja adjektiivi taipuu sen mukaan:

<Example id="ex-3d0ef75d92bc" />

<Interlinear hv="Targār|ien kostōb|on issa." gloss="Targaryen-NOM vahva-TERR.NOM olla-PRS.3SG" fi="Targaryen on vahva." />

<Hv glossed>Kostōba</Hv> ’vahva’ saa päätteen -on, koska sana, jota se kuvaa, on maasukua, vaikka se tarkoittaa ihmistä.

### Vanha ja harvinainen <Term id="deklinaatio">taivutusluokka</Term>

Duolingon ohjeiden mukaan maasuvun sanoissa monikko on -a, ja nominatiivi ja akkusatiivi ovat aina samat. Tavallisista -on-sanoista, kuten <Hv>tegon</Hv> ja <Hv l="lenton">lenton</Hv>, tämä pitää paikkansa. <Hv>Targārien</Hv> on kuitenkin poikkeus. Wikin taivutussivun mukaan se kuuluu neljänteen <Term id="deklinaatio">taivutusluokkaan</Term>, sen -ien-alatyyppiin. Tämä alatyyppi on vanhimpia ja hyvin harvinainen, ja sitä käytetään lähinnä nimissä. Siinä akkusatiivilla on oma muotonsa, ja monikko päättyy -ī:hin:

<Paradigm lemma="targārien" cases="nom,acc" numbers="sg,pl" />

Akkusatiivi <Hv>Targārī</Hv> erottuu nominatiivista. Tällä kertaa subjektin näkee siis suoraan Targaryenia tarkoittavan sanan omasta muodosta:

<Practice hv="Targārien vale urnes." fi="Targaryen näkee miehen." />

<Practice hv="Vala Targārī urnes." fi="Mies näkee Targaryenin." />

### Koko suku

Kun Targaryeneista puhutaan yhtenä joukkona, sarjassa käytetään muotoa <Hv>Targārior</Hv>:

<Line id="hotd-s2e04-001" />

<Hv fi="Targaryenit">Targārior</Hv> on kollektiivi, joka tarkoittaa koko sukua yhtenä kokonaisuutena. Wikin sukusivun mukaan kaikki kollektiivit päättyvät r:ään suvusta riippumatta. Kollektiivi on luku, jota suomessa ei ole, joten se opetellaan myöhemmin omana aiheenaan. Nyt riittää, että tunnistat sen.

## Lopuksi

Maasuvun säännöt mahtuvat kolmeen kohtaan: päätteissä on n, monikko on -a, ja objekti näyttää samalta kuin subjekti. Lisäksi <Hv>Targārien</Hv> on vanha poikkeus, jolla on oma akkusatiivi. Seuraavalla oppitunnilla on vuorossa genetiivi, ja siinä suomesta on enemmän apua kuin missään tähänastisessa.

## Sanat

<Vocab />

<Check />
